import { memo } from 'react';

import { useChartLayoutLoading } from '../../../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../../../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { getLegendOrientation } from '../../../core/components/chart-layout/utils/orientation.js';
import { ChartLegend } from '../../../core/components/legend/categorical/ChartLegend.js';
import { useLegendSeriesActions } from '../../../core/components/legend/categorical/hooks/useLegendSeriesActions.js';
import { LegendItem } from '../../../core/components/legend/categorical/types/legend.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { useSetState } from '../../hooks/store/selectors.js';
import { useCategoricalBarChartData } from '../../hooks/useCategoricalBarChartData.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';
import type {
  CategoricalBarData,
  WeakMapDimension,
} from '../../types/categorical-bar-chart-data.js';

function getLegendItems(
  categories: CategoricalBarData[],
  dimensionMap: WeakMapDimension,
  uniqueDimensions: string[],
) {
  const dimensionMapCache = new Map();
  const uniqueDimensionsSet = new Set(uniqueDimensions);

  categories.forEach((category) => {
    category.dimensions.forEach((dimension) => {
      const { name } = safeGet(dimensionMap, dimension);

      if (uniqueDimensionsSet.has(name)) {
        if (!dimensionMapCache.has(name)) {
          dimensionMapCache.set(name, {
            dimension,
            values: [],
          });
        }

        dimensionMapCache.get(name).values.push({
          category: category.name,
          value: safeGet(dimensionMap, dimension)?.value,
        });
      }
    });
  });

  const legendItems = Array.from(dimensionMapCache.entries()).map(
    ([uniqueDimensionKey, { dimension, values }]) => {
      const { id, name, color, legendColor } = safeGet(dimensionMap, dimension);
      return {
        ...dimension,
        id,
        name,
        color: legendColor ?? color,
        categoryDimensionValues: values,
        category: undefined,
        value: values.length > 1 ? undefined : dimension.value,
      };
    },
  );

  return legendItems;
}

interface CategoryDimensionValue {
  category: string;
  value: number;
}

export type CategoricalLegendItem = LegendItem & {
  categoryDimensionValues: CategoryDimensionValue[];
};

export const CategoricalBarChartLegend = memo(() => {
  const {
    uniqueDimensions,
    categoricalData: categories,
    dimensionMap,
  } = useCategoricalBarChartData();
  const { orientation: layoutOrientation } = useChartLayoutOrientation();
  const truncationMode = useTruncationMode();
  const legendOrientation = getLegendOrientation(layoutOrientation);
  const isLoading = useChartLayoutLoading();

  const legendItems = getLegendItems(
    categories,
    dimensionMap,
    uniqueDimensions,
  );

  const setState = useSetState();

  const onDimensionHover = (item?: LegendItem) => {
    setState((prev) => ({
      ...prev,
      highlightedDimension: item,
    }));
  };

  const onDimensionClick = (visibleDimension: LegendItem[]) => {
    const visibleSeriesSet = new Set(visibleDimension.map((s) => s.name));

    setState((prev) => ({
      ...prev,
      filteredDimensions: visibleSeriesSet,
    }));
  };

  const legendActions = useLegendSeriesActions<CategoricalLegendItem>(
    legendItems,
    (c) => c.id,
  );

  return (
    <ChartLegend
      items={legendItems}
      orientation={legendOrientation}
      onHover={onDimensionHover}
      onClick={onDimensionClick}
      onSeriesAction={legendActions}
      truncationMode={truncationMode}
      interactionsDisabled={isLoading}
    />
  );
});
